import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import AuthPage from "./app/features/auth/pages/AuthPage";
import LandingPage from "./app/features/auth/pages/LandingPage";
import ResetPasswordPage from "./app/features/auth/pages/ResetPasswordPage";
import DashboardPage from "./app/features/dashboard/pages/DashboardPage";
import DashboardLayout from "./app/shared/components/Layout/DashboardLayout";
import ProfilePage from "./app/features/profile/pages/ProfilePage";
import WhiteLabelPage from "./app/features/profile/pages/WhiteLabelPage";
import ProfileEditPage from "./app/features/settings/pages/ProfileEditPage";
import SettingsLayout from "./app/features/settings/layout/SettingsLayout";
import ChangePasswordPage from "./app/features/settings/pages/ChangePasswordPage";
import AccountLayout from "./app/features/accountManagement/layout/AccountLayout";
import UserManagementPage from "./app/features/userManagement/pages/UserManagementPage";
import LearnerListPage from "./app/features/learnerControl/pages/LearnerListPage";
import InterviewSchedulePage from "./app/features/interviewSchedule/pages/InterviewSchedulePage";
import ScheduleInterviewPage from "./app/features/interviewSchedule/pages/ScheduleInterviewPage";
import AddLearnerPage from "./app/features/learnerControl/pages/AddLearnerPage";
import EditLearnerPage from "./app/features/learnerControl/pages/EditLearnerPage";
import ViewLearnerPage from "./app/features/learnerControl/pages/ViewLearnerPage";
import UploadLearnersPage from "./app/features/learnerControl/pages/UploadLearnersPage";
import InterviewResultsPage from "./app/features/interviewResults/pages/InterviewResultsPage";
import LearnerResultsPage from "./app/features/interviewResults/pages/LearnerResultsPage";
import AdminFeedbackPage from "./app/features/interviewResults/pages/AdminFeedbackPage";
import ResultStatsPage from "./app/features/interviewResults/pages/ResultStatsPage";
import JobSearchListPage from "./app/features/jobSearch/pages/JobSearchListPage";
import JobSearchResultPage from "./app/features/jobSearch/pages/JobSearchResultPage";
import InviteAckPage from "./app/features/inviteAck/pages/InviteAckPage";
import PortfolioListPage from "./app/features/portfolio/pages/PortfolioListPage";
import PortfolioDetailPage from "./app/features/portfolio/pages/PortfolioDetailPage";
import JobDashboardPage from "./app/features/jobDashboard/pages/JobDashboardPage";
import OrganizationsPage from "./app/features/organizations/pages/OrganizationsPage";
import OrganizationFormPage from "./app/features/organizations/pages/OrganizationFormPage";
import OrganizationDetailPage from "./app/features/organizations/pages/OrganizationDetailPage";
import JobOpportunitiesPage from "./app/features/jobOpportunity/pages/JobOpportunitiesPage";
import JobOpportunityFormPage from "./app/features/jobOpportunity/pages/JobOpportunityFormPage";
import JobApplicationsKanbanPage from "./app/features/jobApplications/pages/JobApplicationsKanbanPage";
import JobApplicationsTimelinePage from "./app/features/jobApplications/pages/JobApplicationsTimelinePage";
import JobDetailPage from "./app/features/jobOpportunity/pages/JobDetailPage";
import UploadJDPage from "./app/features/jobOpportunity/pages/UploadJDPage";
import ExpertFeedbackPage from "./app/features/expertFeedback/pages/ExpertFeedbackPage";

import QuestionListPage from "./app/features/questionBank/pages/QuestionListPage";
import QuestionUploadPage from "./app/features/questionBank/pages/QuestionUploadPage";
import QuestionUploadViewPage from "./app/features/questionBank/pages/QuestionUploadViewPage";
import AddQuestionPage from "./app/features/questionBank/pages/AddQuestionPage";
import QuestionDetailPage from "./app/features/questionBank/pages/QuestionDetailPage";
import EditQuestionPage from "./app/features/questionBank/pages/EditQuestionPage";
import AIGenerationRequestsPage from "./app/features/questionBank/pages/AIGenerationRequestsPage";
import AIQuestionReviewPage from "./app/features/questionBank/pages/AIQuestionReviewPage";
import GenerateAIQuestionPage from "./app/features/questionBank/pages/GenerateAIQuestionPage";
import RephraseAIQuestionPage from "./app/features/questionBank/pages/RephraseAIQuestionPage";
import AssessmentListPage from './app/features/assessment/pages/AssessmentListPage';
import CreateAssessmentPage from './app/features/assessment/pages/CreateAssessmentPage';
import EditAssessmentPage from './app/features/assessment/pages/EditAssessmentPage';
import AssessmentResultsPage from './app/features/assessment/pages/AssessmentResultsPage';
import AssessmentCandidateResultPage from './app/features/assessment/pages/AssessmentCandidateResultPage';


import { LearnerProvider } from "./app/features/learnerControl/data/context/LearnerContext";
import { AuthProvider } from "./app/features/auth/data/context/AuthContext";
import { ThemeProvider } from "./app/shared/context/ThemeContext";
import Alert, { AlertProps } from "./app/shared/components/Alert";
import httpService from "./app/services/httpService";
import "./index.css";

function App() {
  const [globalAlert, setGlobalAlert] = useState<AlertProps>({
    show: false,
    title: "",
    message: "",
    onClose: () => setGlobalAlert((prev) => ({ ...prev, show: false })),
  });

  useEffect(() => {
    httpService.setShowAlert((config) => {
      setGlobalAlert({
        ...config,
        onClose: () => {
          if (config.onPrimaryClick) config.onPrimaryClick();
          setGlobalAlert((prev) => ({ ...prev, show: false }));
        },
      });
    });
  }, []);

  return (
    <LearnerProvider>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <Alert {...globalAlert} />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/register" element={<AuthPage />} />
              <Route path="/forgot-password" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/reset_password/:entity_type/:secret"
                element={<ResetPasswordPage />}
              />
              <Route
                path="/set_password/interviewiq_admin_user/:secret"
                element={<ResetPasswordPage title="Create Password" />}
              />
              <Route path="/invite-ack" element={<InviteAckPage />} />
              <Route path="/expert-feedback" element={<ExpertFeedbackPage />} />

              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="learners" element={<LearnerListPage />} />
                <Route path="interviews" element={<InterviewSchedulePage />} />
                <Route
                  path="interviews/schedule"
                  element={<ScheduleInterviewPage />}
                />
                <Route path="learners/add" element={<AddLearnerPage />} />
                <Route path="learners/edit" element={<EditLearnerPage />} />
                <Route path="learners/view" element={<ViewLearnerPage />} />
                <Route
                  path="learners/upload"
                  element={<UploadLearnersPage />}
                />

                {/* Interview Results Routes */}
                <Route path="results" element={<InterviewResultsPage />} />
                <Route
                  path="results/learners"
                  element={<LearnerResultsPage />}
                />
                <Route path="results/stats" element={<ResultStatsPage />} />
                <Route
                  path="results/feedback"
                  element={<AdminFeedbackPage />}
                />

                {/* Job Search Routes */}
                <Route path="job-search" element={<JobSearchListPage />} />
                <Route
                  path="job-search/:id"
                  element={<JobSearchResultPage />}
                />

                {/* Portfolio Routes */}
                <Route path="portfolio" element={<PortfolioListPage />} />
                <Route
                  path="portfolio/detail"
                  element={<PortfolioDetailPage />}
                />

                {/* Job Portal Routes */}
                <Route path="job-dashboard" element={<JobDashboardPage />} />
                {/* Organizations Routes */}
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route
                  path="organizations/new"
                  element={<OrganizationFormPage />}
                />
                <Route
                  path="organizations/edit/:id"
                  element={<OrganizationFormPage />}
                />
                <Route
                  path="organizations/:id"
                  element={<OrganizationDetailPage />}
                />
                <Route
                  path="job-opportunities"
                  element={<JobOpportunitiesPage />}
                />
                <Route
                  path="job-opportunity/upload"
                  element={<UploadJDPage />}
                />
                <Route
                  path="job-opportunity/new"
                  element={<JobOpportunityFormPage />}
                />
                <Route
                  path="job-opportunity/edit/:id"
                  element={<JobOpportunityFormPage />}
                />
                <Route
                  path="job-applications/:opportunityId"
                  element={<JobDetailPage />}
                />
                <Route
                  path="job-applications"
                  element={<JobApplicationsTimelinePage />}
                />

                {/* Question Bank Routes */}
                <Route path="question-bank" element={<QuestionListPage />} />
                <Route
                  path="question-bank/upload"
                  element={<QuestionUploadPage />}
                />
                <Route
                  path="question-bank/upload-status"
                  element={<QuestionUploadViewPage />}
                />
                <Route path="question-bank/add" element={<AddQuestionPage />} />
                <Route
                  path="question-bank/view"
                  element={<QuestionDetailPage />}
                />
                <Route
                  path="question-bank/view/:questionId"
                  element={<QuestionDetailPage />}
                />
                <Route
                  path="question-bank/edit"
                  element={<EditQuestionPage />}
                />
                <Route
                  path="question-bank/edit/:questionId"
                  element={<EditQuestionPage />}
                />
                <Route
                  path="question-bank/ai-generate"
                  element={<GenerateAIQuestionPage />}
                />
                <Route
                  path="question-bank/ai-requests"
                  element={<AIGenerationRequestsPage />}
                />
                <Route
                  path="question-bank/ai-review"
                  element={<AIQuestionReviewPage />}
                />
                <Route
                  path="question-bank/ai-review/:requestId"
                  element={<AIQuestionReviewPage />}
                />
                <Route
                  path="question-bank/ai-rephrase"
                  element={<RephraseAIQuestionPage />}
                />
                <Route
                  path="question-bank/ai-rephrase/:questionId"
                  element={<RephraseAIQuestionPage />}
                />

                {/* Assessment Routes */}
                <Route path="assessments" element={<AssessmentListPage />} />
                <Route path="assessments/create" element={<CreateAssessmentPage />} />
                <Route path="assessments/edit/:id" element={<EditAssessmentPage />} />
                <Route path="assessments/results/:id" element={<AssessmentResultsPage />} />
                <Route path="assessments/:assessmentId/results/:candidateId" element={<AssessmentCandidateResultPage />} />
              </Route>

              <Route path="/account" element={<AccountLayout />}>
                <Route path="info" element={<ProfileEditPage />} />
                <Route path="users" element={<UserManagementPage />} />
              </Route>

              <Route path="/account" element={<AccountLayout />}>
                <Route path="info" element={<ProfileEditPage />} />
                <Route path="users" element={<UserManagementPage />} />
              </Route>

              <Route path="/settings" element={<SettingsLayout />}>
                <Route path="whitelabel" element={<WhiteLabelPage />} />
                <Route
                  path="change-password"
                  element={<ChangePasswordPage />}
                />
              </Route>
            </Routes>
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </LearnerProvider>
  );
}

export default App;
